/**
 * @file typo-kind-meta.ts
 * @description Особенности поля «Цвет текста» по видам элемента (phase19.16,
 * партия SB120, этап 3а; uiux-sb120-stage3 §2.2, §3; db-design-sb120-stage3
 * «Расхождения» п. 1–3, 5):
 *  - фон для контраста: обычно — фон под элементом; плитки «поверх фото» —
 *    фото (числа нет, «проверьте на канве»); кнопка с заливкой — заливка
 *    (своя или «Кнопки» темы): цвет текста кнопки = `typo.main.color`,
 *    «Авто: по заливке» (замок SB83 снят);
 *  - цвет «Авто», если он не «Текст / На тёмном по тону фона»: у кнопки с
 *    заливкой — `pickButtonText` по заливке (как витрина `ButtonElement`),
 *    у контурной и ссылки — цвет ссылки секции (`--sec-link`: акцент, на
 *    тёмном фоне — «На тёмном»);
 *  - подпись «Авто» и пояснение под полем (карточки таблицы на телефоне
 *    цвет не меняют — исключение витрины v4).
 * Цифры подложки не имеют — цвет действует, пояснения нет (п. 1).
 * Этап 3б: категории «поверх фото» — на фото; отзывы и объявления
 * карточками — на карточке сайта (`cardBg`, как shared `onSiteCard`).
 *
 * Реестр (OCP): новый вид с особенностью — запись, без ветки в панели.
 */

import { colorRefHex, pickButtonText, type SectionElement, type SectionElementOf, type SiteColors, type TypoElementKind } from "@chaiberi/shared";

import { autoTextColor } from "../site-fields/color-contrast";

/** Что знает поле цвета о месте текста. */
export interface TypoFieldContext {
  /** Фон под текстом: `#hex`, `"photo"` или неизвестен. */
  sample: string | undefined;
  /** Подпись ячейки «Авто». */
  autoLabel: string;
  /** Цвет «Авто», если он особый; нет — «Текст / На тёмном» по тону фона. */
  autoColor?: string;
  /** Пояснение под полем цвета; `null` — нет. */
  hint: string | null;
}

export const TABLE_STACK_HINT = "В карточках таблицы на телефоне цвет текста не меняется — только на ПК и планшете.";

/** Цвет текста кнопки «Авто»: с заливкой — по заливке, иначе — цвет ссылки секции. */
export function buttonAutoTextColor(element: SectionElementOf<"button">, sample: string | undefined, colors: SiteColors): string | undefined {
  if (element.variant === "primary") {
    const fill = element.fill ? colorRefHex(element.fill, colors) : colors.button;
    try {
      return pickButtonText({ ...colors, button: fill }).color;
    } catch {
      return undefined;
    }
  }
  if (!sample || sample === "photo") return undefined;
  return autoTextColor(sample, colors) === colors.onDark ? colors.onDark : colors.accent;
}

type Meta<K extends TypoElementKind> = (el: SectionElementOf<K>, sample: string, colors: SiteColors) => Partial<TypoFieldContext>;

const META: { readonly [K in TypoElementKind]?: Meta<K> } = {
  tiles: (el) => (el.look === "overlay" ? { sample: "photo" } : {}),
  // Этап 3б: категории «поверх фото» — на фото; отзывы и объявления карточками — на карточке сайта.
  categories: (el) => (el.look === "overlay" ? { sample: "photo" } : {}),
  reviews: (_el, _sample, colors) => ({ sample: colors.cardBg }),
  announcements: (el, _sample, colors) => (el.layout === "cards" ? { sample: colors.cardBg } : {}),
  button: (el, sample, colors) => {
    const autoColor = buttonAutoTextColor(el, sample, colors);
    return el.variant === "primary"
      ? { sample: el.fill ? colorRefHex(el.fill, colors) : colors.button, autoLabel: "Авто: по заливке", autoColor }
      : { autoLabel: "Авто: как ссылка", autoColor };
  },
  table: (el) => (el.phoneLayout === "stack" ? { hint: TABLE_STACK_HINT } : {}),
};

/** Контекст поля цвета для элемента; `sample` — фон под элементом (`sectionBgSample`). */
export function typoFieldContext(element: SectionElement, sample: string, colors: SiteColors): TypoFieldContext {
  const meta = META[element.kind as TypoElementKind] as Meta<TypoElementKind> | undefined;
  const extra = meta ? meta(element as SectionElementOf<TypoElementKind>, sample, colors) : {};
  return { sample, autoLabel: "Авто", hint: null, ...extra };
}
